import { RefObject, useCallback, useMemo } from 'react'
import { Node, Viewport, useReactFlow } from 'reactflow'

const PADDING = 500
const SNAP_BACK_DURATION = 300

type Extent = [[number, number], [number, number]]

function clampViewport(
  vp: Viewport,
  extent: Extent,
  width: number,
  height: number,
): Viewport {
  // Same clamping logic ReactFlow uses internally via d3-zoom translateExtent:
  // viewport.x is clamped so the visible flow-space area stays within the extent.
  const xMin = -(extent[1][0] * vp.zoom - width)
  const xMax = -(extent[0][0] * vp.zoom)
  const yMin = -(extent[1][1] * vp.zoom - height)
  const yMax = -(extent[0][1] * vp.zoom)

  return {
    x: Math.max(Math.min(xMin, xMax), Math.min(Math.max(xMin, xMax), vp.x)),
    y: Math.max(Math.min(yMin, yMax), Math.min(Math.max(yMin, yMax), vp.y)),
    zoom: vp.zoom,
  }
}

export function useElasticViewport(
  nodes: Node[],
  containerRef: RefObject<HTMLDivElement>,
) {
  const { getViewport, setViewport } = useReactFlow()

  const extent = useMemo<Extent | undefined>(() => {
    if (nodes.length === 0) return undefined

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity

    for (const n of nodes) {
      const w = n.width ?? 400
      const h = n.height ?? 400
      minX = Math.min(minX, n.position.x)
      minY = Math.min(minY, n.position.y)
      maxX = Math.max(maxX, n.position.x + w)
      maxY = Math.max(maxY, n.position.y + h)
    }

    return [
      [minX - PADDING, minY - PADDING],
      [maxX + PADDING, maxY + PADDING],
    ]
  }, [nodes])

  const onMoveEnd = useCallback(
    (_event: unknown, viewport: Viewport) => {
      if (!extent) return

      const container = containerRef.current
      if (!container) return

      const { width, height } = container.getBoundingClientRect()
      const clamped = clampViewport(viewport, extent, width, height)

      if (clamped.x !== viewport.x || clamped.y !== viewport.y) {
        setViewport(clamped, { duration: SNAP_BACK_DURATION })
      }
    },
    [extent, containerRef, setViewport],
  )

  return { onMoveEnd }
}
